/**
 * GuideCodeBlock — monospaced block with a copy-to-clipboard button.
 * Intentionally low-chrome: no syntax highlighting yet (not needed for
 * the alert message templates / shell snippets / env-var examples we
 * have in the guide).
 */
import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  /** Plain-text code body. Newlines preserved. */
  children: string;
  /** Optional label shown at the top-left of the block (e.g. "TradingView alert message"). */
  label?: string;
}

const GuideCodeBlock: React.FC<Props> = ({ children, label }) => {
  const { isDark } = useAppTheme();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: 1,
        bgcolor: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.25, py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        fontSize: '0.66rem',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: 'text.secondary',
        fontWeight: 700,
      }}>
        <span>{label || 'code'}</span>
        <Tooltip arrow title={copied ? 'Copied' : 'Copy to clipboard'}>
          <IconButton size="small" onClick={copy} sx={{ p: 0.4 }}>
            {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
          fontSize: '0.78rem',
          lineHeight: 1.5,
          overflowX: 'auto',
          whiteSpace: 'pre',
          color: 'text.primary',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default GuideCodeBlock;
